import {
	faChevronRight,
	faCreditCard,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalPaymentMethodDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalPaymentService from '../services/portalPaymentService';

function methodLabel(method: PortalPaymentMethodDto): string {
	const subtype = method.mPaymentSubtype ?? method.mPaymentType;
	const lastFour = method.mPaymentLastFour ?? '';
	return lastFour !== '' ? `${subtype} ****${lastFour}` : subtype;
}

/** "2029-12-01" (raw date) → "12/2029", matching the payment methods list. */
function formatExpiration(value: string): string {
	const parts = value.split('-');
	if (parts.length >= 2 && parts[0].length === 4) {
		return `${parts[1]}/${parts[0]}`;
	}
	return value;
}

/**
 * Account-home payment summary (PD-14080): shows ONLY the primary payment
 * method (the typical account-dashboard pattern) with a link to the full
 * management page — the wall of saved methods moved there.
 */
export function PrimaryPaymentMethodCard({
	title,
}: {
	title?: string;
} = {}): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token, activeClientId } = usePortalAuth();
	const display = useDisplaySettings();

	const { data } = useQuery({
		// Key on the active client so the card refetches on an account switch.
		queryKey: ['portal-payment-methods', activeClientId],
		queryFn: () => portalPaymentService.getPaymentMethods(token ?? ''),
		enabled: token != null,
		staleTime: 30_000,
	});

	// PD-14239: the payment-methods surface is part of the financial section we
	// hide for hide-pricing buyers. This card is rendered directly in
	// AccountPage's hardcoded default layout (not only via the dispatch, which
	// is already gated), so it must self-guard here too. Hooks above run
	// unconditionally to satisfy rules-of-hooks.
	if (!display.canPayInvoices) {
		return null;
	}

	const methods = data?.data ?? [];
	const designatedPrimary = methods.find((method) => method.mPaymentIsPrimary);
	const primary = designatedPrimary ?? (methods.length > 0 ? methods[0] : null);

	return (
		<button
			type="button"
			onClick={() => {
				void navigate('/account/payment-methods');
			}}
			className="w-full bg-brand-surface border border-brand-border rounded-xl p-4 flex items-center gap-3 text-left hover:border-portal-accent transition-colors min-h-touch"
		>
			<div className="w-10 h-10 rounded-full bg-portal-accent/20 flex items-center justify-center shrink-0">
				<FontAwesomeIcon icon={faCreditCard} className="text-portal-accent" />
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
						{t(title ?? 'Payment Methods')}
					</span>
					{designatedPrimary != null && (
						<span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-portal-accent/15 text-portal-accent">
							{t('Primary')}
						</span>
					)}
				</div>
				<div className="text-xs text-brand-muted truncate">
					{primary != null ? (
						<>
							{t(methodLabel(primary))}
							{primary.mPaymentExpirationDate != null &&
								primary.mPaymentExpirationDate !== '' && (
									<>
										{' · '}
										{t('Exp')}{' '}
										{formatExpiration(primary.mPaymentExpirationDate)}
									</>
								)}
							{methods.length > 1 && (
								<>
									{' · '}
									{methods.length - 1} {t('more on file')}
								</>
							)}
						</>
					) : (
						t('No payment method on file')
					)}
				</div>
			</div>
			<FontAwesomeIcon
				icon={faChevronRight}
				className="text-xs text-gray-300 dark:text-gray-600 shrink-0"
			/>
		</button>
	);
}
